import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  useGetDashboardOrdersQuery,
  useCancelOrderMutation,
  useRescheduleOrderMutation,
  type DashboardOrder,
} from '@/api/dashboardApi';
import { formatUsDate } from '@/utils/date';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Eye,
  X,
  Calendar,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';

const formatCurrency = (amount?: number | string | null, currencyCode?: string | null) => {
  if (amount == null) return "-";
  const numeric = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(numeric)) return "-";
  if (!currencyCode || currencyCode === "MIXED") {
    return `${numeric.toFixed(2)}${currencyCode === "MIXED" ? " (Mixed Currency)" : ""}`;
  }
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${numeric.toFixed(2)} ${currencyCode}`;
  }
};

export default function DashboardOrders() {
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState<DashboardOrder | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [newDate, setNewDate] = useState('');

  const { user } = useSelector((state: RootState) => state.auth);
  const isClientRole = user?.role === "CLIENT" || user?.role === "CLIENT_EMPLOYEE";

  const { data: orders = [], isLoading, error } = useGetDashboardOrdersQuery({
    clientId: user?.clientId,
    role: user?.role,
    email: user?.emailAddress
  });
  const [cancelOrder, cancelResult] = useCancelOrderMutation();
  const [rescheduleOrder, rescheduleResult] = useRescheduleOrderMutation();

  const handleCancelOrder = (order: DashboardOrder) => {
    setSelectedOrder(order);
    setShowCancelDialog(true);
  };

  const confirmCancel = async () => {
    if (selectedOrder) {
      try {
        await cancelOrder({
          appointmentId: selectedOrder.appointmentId,
          role: user?.role,
          clientId: user?.clientId
        }).unwrap();
        setShowCancelDialog(false);
        setSelectedOrder(null);
      } catch (err) {
        console.error('Failed to cancel order:', err);
      }
    }
  };

  const handleReschedule = (order: DashboardOrder) => {
    setSelectedOrder(order);
    setNewDate('');
    setShowRescheduleDialog(true);
  };

  const confirmReschedule = async () => {
    if (selectedOrder && newDate) {
      try {
        await rescheduleOrder({
          appointmentId: selectedOrder.appointmentId,
          newDate,
          role: user?.role,
          clientId: user?.clientId
        }).unwrap();
        setShowRescheduleDialog(false);
        setSelectedOrder(null);
        setNewDate('');
      } catch (err) {
        console.error('Failed to reschedule order:', err);
      }
    }
  };

  const getStatusVariant = (status: string): "secondary" | "default" | "outline" | "destructive" => {
    switch (status.toUpperCase()) {
      case 'SCHEDULED':
      case 'PENDING':
        return 'secondary';
      case 'COMPLETED':
        return 'default';
      case 'CANCELLED':
        return 'destructive';
      case 'NO_SHOW':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeStyles = (status: string) => {
    switch (status.toUpperCase()) {
      case 'SCHEDULED':
      case 'PENDING':
        return 'bg-amber-100 text-amber-700 hover:bg-amber-100 border-0';
      case 'IN_PROGRESS':
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-700 hover:bg-blue-100 border-0';
      case 'COMPLETED':
        return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0';
      case 'CANCELLED':
        return 'bg-rose-100 text-rose-700 hover:bg-rose-100 border-0';
      case 'NO_SHOW':
        return 'bg-orange-100 text-orange-700 hover:bg-orange-100 border-0';
      default:
        return '';
    }
  };

  const isManageableStatus = (status: string) =>
    !['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(status.toUpperCase());

  const getVisitDate = (order: DashboardOrder) => order.visitDate ?? order.appointmentDate;

  const formatVisitDate = (value?: string) =>
    value ? formatUsDate(value) : 'Not scheduled';

  const visibleOrders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return orders
      .filter((order) => {
        const statusMatch = !filterStatus || order.orderStatus?.toUpperCase() === filterStatus;
        if (!statusMatch) return false;
        if (!query) return true;
        const haystack = [
          order.candidateName,
          order.orderNumber,
          order.clientName,
          order.clinicName,
          order.orderSubmittedBy,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
      .sort((a, b) => {
        const dateA = new Date(a.orderDate).getTime();
        const dateB = new Date(b.orderDate).getTime();
        if (Number.isFinite(dateA) && Number.isFinite(dateB) && dateA !== dateB) {
          return dateB - dateA;
        }
        return (b.appointmentId ?? 0) - (a.appointmentId ?? 0);
      });
  }, [orders, filterStatus, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchTerm, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(visibleOrders.length / rowsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return visibleOrders.slice(start, start + rowsPerPage);
  }, [visibleOrders, currentPage, rowsPerPage]);

  const startIndex = visibleOrders.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endIndex = Math.min(currentPage * rowsPerPage, visibleOrders.length);

  return (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <CardHeader className="space-y-4">
          <CardTitle className="text-base md:text-lg">
            {filterStatus ? `${filterStatus.replace('_', ' ')} Orders` : 'All Orders'} List
          </CardTitle>
          <div className="grid gap-3 md:grid-cols-[220px_1fr_120px]">
            <Select
              value={filterStatus ?? 'ALL'}
              onValueChange={(val) => setFilterStatus(val === 'ALL' ? undefined : val)}
            >
              <SelectTrigger className="h-11 rounded-xl border-slate-200 bg-white">
                <SelectValue placeholder="All Orders" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="ALL">All Orders</SelectItem>
                <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative group">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search candidate, order #, client, clinic..."
                className="h-11 w-full rounded-xl border-slate-200 bg-white pl-10 pr-3 focus-visible:ring-blue-500"
              />
            </div>

            <Select
              value={String(rowsPerPage)}
              onValueChange={(val) => setRowsPerPage(Number(val))}
            >
              <SelectTrigger className="h-11 rounded-xl border-slate-200 bg-white">
                <SelectValue placeholder="10 / page" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="20">20 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-900">Error Loading Orders</p>
                <p className="text-sm text-red-700">Failed to load orders. Please try again later.</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : paginatedOrders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">No orders found</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Candidate Name</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead>Client Name</TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Order Date</TableHead>
                    <TableHead>Clinic Name/Phone</TableHead>
                    <TableHead>Visit Date</TableHead>
                    {!isClientRole && <TableHead>Total</TableHead>}
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedOrders.map((order) => (
                    <TableRow key={order.appointmentId} className="hover:bg-slate-50/70">
                      <TableCell className="text-slate-400 font-medium">{(currentPage - 1) * rowsPerPage + paginatedOrders.indexOf(order) + 1}</TableCell>
                      <TableCell className="font-medium">
                        <Link
                          to={`${isClientRole ? '/client' : '/super-admin'}/dashboard/orders/${order.appointmentId}`}
                          className="text-primary hover:underline"
                        >
                          {order.candidateName}
                        </Link>
                      </TableCell>
                      <TableCell>{order.orderSubmittedBy || '-'}</TableCell>
                      <TableCell>{order.clientName || '-'}</TableCell>
                      <TableCell>{order.orderNumber || '-'}</TableCell>
                      <TableCell>
                        <Badge
                          className={`font-bold px-3 py-0.5 rounded-full ${getStatusBadgeStyles(order.orderStatus)}`}
                          variant={getStatusVariant(order.orderStatus)}
                        >
                          {order.orderStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {formatUsDate(order.orderDate)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{order.clinicName}</span>
                          {order.clinicPhone && (
                            <span className="text-[10px] text-slate-500 font-bold">{order.clinicPhone}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatVisitDate(getVisitDate(order))}</TableCell>
                      {!isClientRole && (
                        <TableCell className="text-sm font-semibold">
                          {formatCurrency(order.totalCost, order.currencyCode)}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" asChild title="View Details">
                            <Link to={`${isClientRole ? '/client' : '/super-admin'}/dashboard/orders/${order.appointmentId}`}>
                              <Eye className="w-4 h-4" />
                            </Link>
                          </Button>

                          {!isClientRole && isManageableStatus(order.orderStatus) && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReschedule(order)}
                                title="Reschedule"
                              >
                                <Calendar className="w-4 h-4" />
                              </Button>

                              <Dialog
                                open={
                                  showRescheduleDialog &&
                                  selectedOrder?.appointmentId === order.appointmentId
                                }
                                onOpenChange={setShowRescheduleDialog}
                              >
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Reschedule Visit Date</DialogTitle>
                                    <DialogDescription>
                                      Select a new visit date and time
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div>
                                      <label className="text-sm font-medium">New Visit Date</label>
                                      <div className="mt-2">
                                        <DatePicker
                                          value={newDate}
                                          onChange={setNewDate}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <Button
                                      variant="outline"
                                      onClick={() => setShowRescheduleDialog(false)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={confirmReschedule}
                                      disabled={!newDate || rescheduleResult.isLoading}
                                    >
                                      {rescheduleResult.isLoading ? 'Rescheduling...' : 'Reschedule'}
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                            </>
                          )}

                          {isManageableStatus(order.orderStatus) && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancelOrder(order)}
                                title="Cancel Order"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="w-4 h-4" />
                              </Button>

                              <Dialog
                                open={
                                  showCancelDialog &&
                                  selectedOrder?.appointmentId === order.appointmentId
                                }
                                onOpenChange={setShowCancelDialog}
                              >
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Cancel Order</DialogTitle>
                                    <DialogDescription>
                                      Are you sure you want to cancel this order? This action cannot be
                                      undone.
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                    <p className="text-sm text-red-700">
                                      <strong>Candidate:</strong> {selectedOrder?.candidateName}
                                    </p>
                                    <p className="text-sm text-red-700">
                                      <strong>Order #:</strong> {selectedOrder?.orderNumber}
                                    </p>
                                  </div>
                                  <DialogFooter>
                                    <Button
                                      variant="outline"
                                      onClick={() => setShowCancelDialog(false)}
                                    >
                                      Keep Order
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      onClick={confirmCancel}
                                      disabled={cancelResult.isLoading}
                                    >
                                      {cancelResult.isLoading ? 'Cancelling...' : 'Cancel Order'}
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-4 flex flex-col gap-3 border-t pt-4 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {startIndex}-{endIndex} of {visibleOrders.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[90px] text-center text-sm font-medium text-slate-700">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
